<template>
  <div class="flex items-center justify-between py-3 px-5">
    <!-- Left: timecode + live -->
    <div class="flex items-center gap-3">
      <template v-if="mode === 'live'">
        <span class="font-data text-base font-semibold text-gray-900">{{ currentDatetime }}</span>
        <button @click="$emit('go-live')" :class="['px-3 py-1.5 rounded-md text-xs font-bold transition-colors', isLive ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-400 hover:bg-gray-300']">
          <span class="inline-block w-2 h-2 rounded-full mr-1" :class="isLive ? 'bg-white' : 'bg-red-500'" /> LIVE
        </button>
      </template>
      <template v-else>
        <span class="font-data text-base font-semibold text-gray-900">{{ formatTimeFull(currentTime) }}</span>
        <span class="text-gray-300">/</span>
        <span class="font-data text-base text-gray-400">{{ formatTimeFull(duration) }}</span>
      </template>
      <!-- Volume -->
      <div class="flex items-center gap-2 ml-3">
        <button @click="$emit('toggle-mute')" class="p-1.5 text-gray-500 hover:text-gray-900 transition-colors">
          <svg v-if="muted || volume === 0" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
          <svg v-else class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>
        </button>
        <input type="range" min="0" max="1" step="0.01" :value="volume" @input="$emit('volume', Number($event.target.value))" class="volume-slider" />
      </div>
    </div>

    <!-- Center: playback -->
    <div class="flex items-center gap-1">
      <button @click="$emit('skip', -10)" class="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" title="Back 10s">
        <svg class="w-5 h-5 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="19 20 9 12 19 4 19 20" /><line x1="5" y1="19" x2="5" y2="5"/></svg>
      </button>
      <button @click="$emit('toggle-play')" class="w-14 h-14 rounded-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] flex items-center justify-center transition-colors shadow-lg shadow-[var(--color-primary-glow)]">
        <svg v-if="playing" class="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
        <svg v-else class="w-6 h-6 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
      </button>
      <button @click="$emit('skip', 10)" class="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" title="Forward 10s">
        <svg class="w-5 h-5 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19"/></svg>
      </button>
    </div>

    <!-- Right: mark in/out + zoom -->
    <div class="flex items-center gap-2">
      <!-- Ad-region affordance hint: only shown when the source has any
           commercial breaks. Sits right before the IN button so users
           notice it while looking at the mark controls. -->
      <div
        v-if="commercialBreaks.length"
        class="hidden xl:flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-[11px] font-medium text-amber-700 mr-1"
        title="Right-click any amber ad region on the timeline to create a selection for it or the content before / after"
      >
        <span class="w-2.5 h-2.5 rounded-sm bg-amber-200 border border-amber-400" />
        Ad break
        <span class="text-amber-500/70">·</span>
        <span class="text-amber-600/80">right-click for options</span>
      </div>
      <button @click="$emit('mark-in')" class="px-3 py-2 rounded-md text-sm font-medium bg-green-50 text-green-600 hover:bg-green-100 transition-colors" title="Mark In (I)">
        <svg class="w-3.5 h-3.5 inline mr-1 -mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="15 3 21 3 21 9"/><line x1="21" y1="3" x2="14" y2="10"/></svg>
        IN
      </button>
      <button @click="$emit('mark-out')" :disabled="markInTime === null" class="px-3 py-2 rounded-md text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-30" title="Mark Out (O)">
        <svg class="w-3.5 h-3.5 inline mr-1 -mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="9 21 3 21 3 15"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
        OUT
      </button>
      <button v-if="markInTime !== null" @click="$emit('clear-selection')" class="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors" title="Clear">
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <div class="w-px h-6 bg-gray-200 mx-1" />
      <button @click="$emit('zoom-in')" class="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors" title="Zoom In">
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
      </button>
      <button @click="$emit('zoom-out')" class="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors" title="Zoom Out">
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
      </button>
      <button @click="$emit('zoom-fit')" class="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors" title="Fit">
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'EditorControls',
  emits: ['toggle-play', 'skip', 'mark-in', 'mark-out', 'clear-selection', 'go-live', 'toggle-mute', 'volume', 'zoom-in', 'zoom-out', 'zoom-fit'],

  data() {
    return { now: new Date(), clockInterval: null }
  },

  computed: {
    ...mapState('editor', ['currentTime', 'duration', 'playing', 'mode', 'isLive', 'volume', 'muted', 'markInTime', 'commercialBreaks']),
    currentDatetime() {
      const d = this.now
      const h = String(d.getHours()).padStart(2, '0')
      const m = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${h}:${m}:${s}`
    }
  },

  mounted() {
    this.clockInterval = setInterval(() => { this.now = new Date() }, 1000)
  },

  beforeUnmount() {
    if (this.clockInterval) clearInterval(this.clockInterval)
  },

  methods: {
    formatTimeFull(s) {
      if (s == null || isNaN(s)) return '00:00:00'
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = Math.floor(s % 60)
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.volume-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 72px;
  height: 5px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  cursor: pointer;
}
.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-primary);
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.volume-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-primary);
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
</style>
